import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { applyThemeToDocument } from '../utils/themePalette';

const DEFAULT_NAME = import.meta.env.VITE_APP_NAME || 'BlackTape';
const DEFAULT_COLOR = import.meta.env.VITE_PRIMARY_COLOR || '#004852';

function publicApiBase() {
  const raw = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api/merchant').replace(/\/$/, '');
  if (raw.endsWith('/merchant')) return raw.slice(0, -'/merchant'.length);
  if (raw.endsWith('/api')) return raw;
  return raw.replace(/\/api\/merchant$/, '/api');
}

const BrandingContext = createContext({
  loading: true,
  siteName: DEFAULT_NAME,
  logoUrl: null,
  faviconUrl: null,
  primaryColor: DEFAULT_COLOR,
  suspended: false,
  found: false,
  refreshBranding: () => {},
  applyBrandingPayload: () => {},
});

function applyBrandingToDom(branding) {
  if (!branding) return;
  applyThemeToDocument(branding.primaryColor || DEFAULT_COLOR);
  if (branding.siteName) document.title = branding.siteName;
  if (branding.faviconUrl) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.faviconUrl;
  }
}

function normalizeBranding(data = {}) {
  return {
    loading: false,
    siteName: data.siteName || DEFAULT_NAME,
    logoUrl: data.logoUrl || null,
    faviconUrl: data.faviconUrl || null,
    primaryColor: data.primaryColor || DEFAULT_COLOR,
    suspended: Boolean(data.suspended),
    found: Boolean(data.found),
    slug: data.slug || null,
  };
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState({
    loading: true,
    siteName: DEFAULT_NAME,
    logoUrl: null,
    faviconUrl: null,
    primaryColor: DEFAULT_COLOR,
    suspended: false,
    found: false,
    slug: null,
  });

  const applyBrandingPayload = useCallback((data) => {
    if (!data) return;
    const next = normalizeBranding(data);
    setBranding(next);
    applyBrandingToDom(next);
    if (data.slug) localStorage.setItem('brandSlug', data.slug);
  }, []);

  const refreshBranding = useCallback(async () => {
    const slug =
      localStorage.getItem('brandSlug') ||
      new URLSearchParams(window.location.search).get('slug') ||
      undefined;

    try {
      const res = await axios.get(`${publicApiBase()}/public/branding`, {
        params: {
          host: window.location.hostname,
          panel: 'merchant',
          ...(slug ? { slug } : {}),
        },
      });
      applyBrandingPayload(res.data);
    } catch {
      const fallback = normalizeBranding({});
      setBranding(fallback);
      applyBrandingToDom(fallback);
    }
  }, [applyBrandingPayload]);

  useEffect(() => {
    applyThemeToDocument(DEFAULT_COLOR);
    refreshBranding();
  }, [refreshBranding]);

  useEffect(() => {
    const onApply = (e) => {
      if (e?.detail) applyBrandingPayload(e.detail);
    };
    window.addEventListener('blacktape:branding-apply', onApply);
    window.addEventListener('blacktape:branding-refresh', refreshBranding);
    return () => {
      window.removeEventListener('blacktape:branding-apply', onApply);
      window.removeEventListener('blacktape:branding-refresh', refreshBranding);
    };
  }, [applyBrandingPayload, refreshBranding]);

  return (
    <BrandingContext.Provider
      value={{ ...branding, refreshBranding, applyBrandingPayload }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
